import { DualInfo, RichText, Text } from "@kvix/ui";
import {
  Box,
  CircularProgress,
  Container,
  Link,
  makeStyles,
  Theme,
} from "@material-ui/core";
import React, { Fragment, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAsync } from "react-use";
import { AsyncState } from "react-use/lib/useAsyncFn";
import { Investor } from "../../../../cms/models";
import { InvestorAdapter } from "../../../../cms/models/adapters/single/Investor";
import PersonList from "../../../../cms/models/common/PersonList";
import Table from "../../../../cms/models/components/Common/Table";
import { Footer } from "../../../Footer";
import { Page } from "../../../partials/Page";
import { CmsPageCover } from "../Cover";
import { MfnFeed } from "./MfnFeed";

const STRAPI_URL = window.config.STRAPI_URL;

const useStyles = makeStyles<Theme, any>((theme) => ({
  root: {
    marginBottom: (props: any) => (props.noMargin ? 0 : theme.spacing(6)),
    "& h1": {
      marginBottom: 40,
    },
  },
  titleRow: {
    display: "flex",
    alignItems: "baseline",
    width: "100%",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 700,
  },
  description: {
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
}));

const useInvestor = (): AsyncState<Investor> => {
  return useAsync(async () => {
    const response = await fetch(
      `${STRAPI_URL}/api/investor-relation?populate=deep`
    );

    if (response.ok) {
      const json = await response.json();

      return await InvestorAdapter(json);
    } else {
      console.error({ status: response.status, message: response.statusText });
    }
  });
};

const InvestorPage: React.FC = memo((props) => {
  const investorAsync = useInvestor();

  const classes = useStyles(props);
  const { t, ready } = useTranslation(["investor", "seo"]);

  const investor = useMemo(() => {
    if (!investorAsync?.value) {
      return null;
    }
    return investorAsync.value;
  }, [investorAsync]);

  if (!ready) {
    return null;
  }

  return (
    <Page
      meta={
        investor
          ? {
              title: t("seo:investor.title", {
                title: investor.title,
              }),
              description: t("seo:investor.description"),
              ogTitle: investor.title,
              ogImage: investor.cover.url,
            }
          : {}
      }
    >
      {!investorAsync.loading ? (
        <Fragment>
          {investor.cover && (
            <Box mt={-16} mb={8}>
              <CmsPageCover cover={investor.cover} />
            </Box>
          )}
          <Container maxWidth="lg">
            <div className={classes.root}>
              <Text variant="h1">{investor.title}</Text>
              <RichText text={investor.topContent} noPadding />
              <MfnFeed type="ca" />
              <MfnFeed type="report" />
              <Table data={investor.calendar} />
              <Table data={investor.ownerStructure} />
              {investor.PeopleList.map((p, index) => {
                return <PersonList key={index} {...p} />;
              })}
              <Link href={'https://kvix.tv/ipo'} style={{ fontSize: '28px' }} target="_blank">Memorandum</Link>
              {investor.DualInfo && (
                <DualInfo
                  id={investor?.DualInfo.id}
                  Column1={investor?.DualInfo.Column1}
                  Column2={investor?.DualInfo.Column2}
                />
              )}
              <RichText text={investor.bottomContent} noPadding />
            </div>
          </Container>
        </Fragment>
      ) : (
        <CircularProgress />
      )}
      <Footer />
    </Page>
  );
});

export default InvestorPage;
